/** Regelversion 11: Überzeugungsarbeit und Wirtschaftsordnung. */
import { usePref } from '../../lib/pref';
import { useGame, useS } from '../../store/game';
import { Box, Panel, Tag } from '../../components/ui';
import { AXN } from '../../content/axes';
import { SYS } from '../../content/order11';
import { pub, scaleLabel } from '../../engine/opinion';
import { axes } from '../../engine/axes';
import { busyOf } from '../../engine/coalition';
import { capOf } from '../../engine/adm';
import { PERS_MONTHS, againstMajority, pers, persOn, wo, woEffects, woLabel, woOf, woOn } from '../../engine/order11';
import { sysState } from '../../engine/order11ui';
import { de, sg, dateLabel } from '../../engine/util';

/** Skala der Wirtschaftsordnung von −100 bis +100. */
export function WoScale({ v, d }: { v: number; d?: number }) {
  const x = (n: number) => `${(n + 100) / 2}%`;
  return (
    <div className="woscale" role="img" aria-label={`Wirtschaftsordnung: ${woLabel(v)} (${de(v, 0)})`}>
      <div className="wobar">{[-60, -25, 25, 60].map((n) => <i key={n} className="wotick" style={{ left: x(n) }} />)}
        {d ? <span className="woshift" style={{ left: x(Math.min(v, v + d)), width: `${Math.abs(d) / 2}%` }} /> : null}
        <b className="wodot" style={{ left: x(v) }} /></div>
      <div className="wolabels"><span>Staatswirtschaft</span><span>Soziale Marktwirtschaft</span><span>Laissez-faire</span></div>
    </div>
  );
}

/** Im Gesetzentwurf: Verschiebung der Wirtschaftsordnung, EU-Risiko. */
export function WoDraftBox({ fx }: { fx: any }) {
  if (!woOn()) return null;
  const d = woOf(fx), v = wo(); if (Math.abs(d) < 1 && !fx.eu) return null;
  return (
    <Box label={`Wirtschaftsordnung ${sg(d, 0)}`}>
      <WoScale v={v} d={d} />
      <span className="sub">Danach: {woLabel(Math.max(-100, Math.min(100, v + d)))}. {d < 0 ? 'Richtung Staat: weniger Wachstum, kleinere soziale Schere, ab −35 Kapitalflucht und Proteste der Wirtschaft.' : 'Richtung Markt: etwas mehr Wachstum, größere soziale Schere, ab +35 Streiks und mehr Krisenrisiko.'}</span>
      {fx.eu ? <span className="warn">Brüssel prüft das Beihilfe- und Binnenmarktrecht: Risiko eines Vertragsverletzungsverfahrens {Math.round(fx.eu * 100)} %.</span> : null}
    </Box>
  );
}

/** Reiter „Lage“: Wirtschaftsordnung und Systemgesetze. */
export function OrdnungPanel() {
  const S = useS(); const { dispatch } = useGame();
  const [open, setOpen] = usePref('kabinett-ordnung-offen', false);
  if (!woOn()) return null;
  const v = wo(), e = woEffects(), full = busyOf('YOU') >= capOf('YOU');
  const title = <button type="button" className="ptoggle" aria-expanded={open} onClick={() => setOpen(!open)}>Wirtschaftsordnung</button>;
  if (!open) return <Panel title={title} aside={`${woLabel(v)} · eingeklappt`} className="ordnung closed"><WoScale v={v} /><span className="sub">Gesetze, die das Land Richtung Staat oder Richtung Markt umbauen. <button type="button" className="linkb" onClick={() => setOpen(true)}>Aufklappen</button></span></Panel>;
  const row = (side: -1 | 1) => SYS.filter((x) => x.side === side).map((X) => { const s = sysState(X.id); return (
    <button type="button" key={X.id} className="small" disabled={s.busy || s.done || !s.mine || full} title={s.done ? 'Gilt bereits.' : s.busy ? 'Läuft bereits.' : !s.mine ? 'Das zuständige Ministerium führt der Koalitionspartner.' : full ? 'Eure Ministerien sind ausgelastet.' : X.law.d} onClick={() => dispatch('draftWo' as any, X.id)}>{X.law.t}{s.done ? <span className="sub"> · gilt</span> : s.busy ? <span className="sub"> · läuft</span> : null}</button>); });
  return (
    <Panel title={title} aside={woLabel(v)} className="ordnung">
      <WoScale v={v} />
      <div className="tags">
        <Tag good={e.gr >= 0}>Wachstum {sg(e.gr, 2)} Pkt</Tag>
        <Tag good={e.gini <= 0}>Soziale Schere {sg(e.gini, 1)}</Tag>
        {e.flight && <Tag good={false}>Kapitalflucht</Tag>}
        {e.strikes && <Tag good={false}>Streiks und Krisenrisiko</Tag>}
      </div>
      <p className="sub">Jedes Gesetz verschiebt die Ordnung ein wenig, diese Systemgesetze stark. Beide Richtungen lassen sich gehen, beide haben ihren Preis: Richtung Staat sinken Wachstum und soziale Schere, ab −35 fliehen Kapital und Unternehmen. Richtung Markt wächst die Wirtschaft etwas schneller, aber die Schere geht auf, ab +35 drohen Streiks und Krisen. Ab ±50 organisiert sich ein Gegenlager. Karlsruhe prüft Eigentum und Sozialstaat, Brüssel das Binnenmarktrecht.</p>
      <div className="worows">
        <div><span className="label">Richtung Staat</span><div className="agacts">{row(-1)}</div></div>
        <div><span className="label">Richtung Markt</span><div className="agacts">{row(1)}</div></div>
      </div>
      {(S as any).woEu?.length ? <span className="warn">Brüssel prüft: {(S as any).woEu.map((x: any) => x.t).join(', ')}.</span> : null}
    </Panel>
  );
}

/** Kommunikation: Überzeugungsarbeit. */
export function PersuadeSec() {
  const S = useS(); const { dispatch } = useGame();
  if (!persOn()) return null;
  const P = pers(), pts = S.com?.pts ?? 0;
  const list = axes().filter((a) => AXN[a] && Math.abs((S.you[a] ?? 0) - pub(a)) >= .5).sort((a, b) => Math.abs(S.you[b] - pub(b)) - Math.abs(S.you[a] - pub(a))).slice(0, 8);
  return (
    <div className="comsec"><span className="label">Überzeugungsarbeit</span>
      {P ? <><span className="sub">Ihr werbt bis {dateLabel(P.until)} für eure Haltung zum Thema <b>{AXN[P.ax]}</b>. Bevölkerung jetzt: <b>{scaleLabel(pub(P.ax))}</b> ({sg(pub(P.ax), 2)}), euer Programm: <b>{scaleLabel(S.you[P.ax])}</b>. {P.paused ? 'Diesen Monat war kein Termin frei, die Kampagne ruhte.' : 'Jeder Monat kostet einen Termin.'}{(S as any).polar >= 35 ? ' Das Land ist gespalten: Die Kampagne wirkt nur halb, die andere Seite verhärtet sich.' : ''}</span>
        <div className="tags"><button type="button" className="small" onClick={() => dispatch('comPersuadeStop' as any)}>Kampagne beenden</button></div></>
        : <><span className="sub">Bespielt ein Meinungsthema über bis zu {PERS_MONTHS} Monate. Die Stimmung im Land wandert langsam zu eurer Position. Jeder Monat kostet einen Termin. Gegen eine klare Mehrheit kostet es erst Zustimmung, und es dauert, bis sich etwas bewegt.</span>
          <div className="tags">{list.map((a) => <button type="button" key={a} className="small" disabled={pts < 1} onClick={() => dispatch('comPersuade' as any, a)}>{AXN[a]} <span className="sub">{scaleLabel(pub(a))} → {scaleLabel(S.you[a])}{againstMajority(a, Math.sign(S.you[a] - pub(a))) ? ' · gegen die Mehrheit' : ''}</span></button>)}</div></>}
    </div>
  );
}
